import { useQuery } from '@tanstack/react-query';
import { getJson } from './api';

/**
 * One event's Income & Expense Statement and every expense line on it.
 * Mirrors `apps/api/src/api/v3/profitability/event-pnl.ts`. Behind
 * view_accounting: lines carry facilitator and assistant pay.
 */

export const EVENT_PNL_PATH = '/analytics/accounting/events/:opportunityId';

export function eventPnlPath(opportunityId: string): string {
  return EVENT_PNL_PATH.replace(':opportunityId', encodeURIComponent(opportunityId));
}

/** The API's code when the opportunity has no statement. */
export const EVENT_PNL_NOT_FOUND = 'NOT_FOUND';

// ─── Wire types ──────────────────────────────────────────────────────────────

export interface EventPnlPerson {
  readonly contactId: string;
  readonly name: string;
  readonly email: string;
  readonly userId: string | null;
}

export interface EventPnlHeader {
  readonly opportunityId: string;
  readonly opportunityName: string;
  readonly accountName: string;
  readonly program: string;
  readonly eventDate: string | null;
  readonly participants: number | null;
  readonly stageName: string;
  readonly isVirtual: boolean;
  readonly ownerName: string;
  readonly contractNumber: string;
  readonly eventId: string | null;
}

export interface EventPnlTotals {
  readonly revenue: number;
  readonly itemizedExpenses: number;
  readonly commission: number;
  readonly ipadFees: number;
  readonly contribution: number;
  readonly margin: number | null;
  readonly contributionPerParticipant: number | null;
  readonly targetMargin: number;
  readonly marginVsTarget: number | null;
  readonly linesTotal: number;
  readonly unexplained: number;
}

export interface EventPnlLine {
  readonly expenseId: string;
  readonly category: string;
  readonly description: string;
  readonly person: EventPnlPerson | null;
  readonly vendorName: string;
  readonly quantity: number | null;
  readonly costEach: number | null;
  readonly cost: number | null;
  readonly shippingCost: number | null;
  readonly shippingMethod: string;
  readonly trackingNumber: string;
  readonly total: number;
  readonly createdAt: string | null;
  readonly receipt: { readonly submissionId: string; readonly receivedAt: string; readonly receiptUrl: string | null } | null;
}

export interface EventPnlCategory {
  readonly category: string;
  readonly subtotal: number;
  readonly share: number | null;
  readonly lines: readonly EventPnlLine[];
}

export interface EventPnl {
  readonly header: EventPnlHeader;
  readonly statement: { readonly statementId: string; readonly statementName: string };
  readonly totals: EventPnlTotals;
  readonly categories: readonly EventPnlCategory[];
}

// ─── Read ────────────────────────────────────────────────────────────────────

export const EVENT_PNL_QUERY_KEY = (opportunityId: string) => ['event-pnl', opportunityId] as const;

/** Statements copy over from Salesforce hourly: a minute's cache is plenty. */
const EVENT_PNL_STALE_MS = 60_000;

/**
 * The event's P&L. Pass `enabled: false` for viewers without view_accounting
 * so the request is never made.
 */
export function useEventPnl(opportunityId: string, enabled = true) {
  return useQuery({
    queryKey: EVENT_PNL_QUERY_KEY(opportunityId),
    queryFn: ({ signal }) =>
      getJson<EventPnl>(`/accounting/events/${encodeURIComponent(opportunityId)}/pnl`, undefined, signal),
    enabled: enabled && opportunityId !== '',
    staleTime: EVENT_PNL_STALE_MS,
  });
}

// ─── Flattened rows (sortRows / filterRows read top-level fields) ────────────

export interface EventPnlLineRow {
  readonly expenseId: string;
  readonly category: string;
  readonly description: string;
  readonly personName: string;
  readonly contactId: string;
  readonly vendorName: string;
  readonly quantity: number | null;
  readonly costEach: number | null;
  readonly cost: number | null;
  readonly shippingCost: number | null;
  readonly total: number;
  readonly createdAt: string | null;
  readonly line: EventPnlLine;
}

export function toLineRows(categories: readonly EventPnlCategory[]): EventPnlLineRow[] {
  return categories.flatMap((category) =>
    category.lines.map((line) => ({
      expenseId: line.expenseId,
      category: line.category,
      description: line.description,
      personName: line.person ? line.person.name || line.person.email : '',
      contactId: line.person?.contactId ?? '',
      vendorName: line.vendorName,
      quantity: line.quantity,
      costEach: line.costEach,
      cost: line.cost,
      shippingCost: line.shippingCost,
      total: line.total,
      createdAt: line.createdAt,
      line,
    })),
  );
}

/** "Oct 5, 2026" from an ISO timestamp. */
export function createdLabel(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "4 points under the 65% target", "6 points over". */
export function marginVsTargetText(totals: Pick<EventPnlTotals, 'marginVsTarget' | 'targetMargin'>): string {
  const target = `${Math.round(totals.targetMargin * 100)}% target`;
  if (totals.marginVsTarget === null) return `No revenue to measure against the ${target}`;
  const points = Math.round(totals.marginVsTarget * 100);
  if (points === 0) return `On the ${target}`;
  const unit = Math.abs(points) === 1 ? 'point' : 'points';
  return `${Math.abs(points)} ${unit} ${points < 0 ? 'under' : 'over'} the ${target}`;
}
